import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Lock, RefreshCw, Trash2, Mail, Phone, Building2, MapPin, Calendar, LogOut, Inbox, ShoppingBag, Package, Ticket, MapPinned } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import AdminCatalog from "./AdminCatalog";
import AdminOrders from "./AdminOrders";
import AdminCoupons from "./AdminCoupons";
import Autocomplete from "@/components/Autocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "tcsw_admin_pw";
const TAB_KEY = "tcsw_admin_tab";

const STATUSES = ["new", "contacted", "won", "closed"];
const STATUS_STYLES = {
  new: "bg-purple/20 text-purple-hi border-purple-hi/40",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-400/40",
  won: "bg-gold/20 text-gold border-gold/50",
  closed: "bg-white/10 text-white/50 border-white/15",
};

export default function Admin() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [tab, setTab] = useState(() => sessionStorage.getItem(TAB_KEY) || "quotes");
  const [unread, setUnread] = useState({ orders: 0, quotes: 0 });

  useEffect(() => { sessionStorage.setItem(TAB_KEY, tab); }, [tab]);

  // Poll for new orders/quotes since the user's last visit. The "last seen"
  // timestamp is persisted in localStorage per-tab and bumped whenever the
  // admin opens the corresponding tab.
  const LAST_SEEN_KEY = "tcsw_admin_last_seen_v1";
  useEffect(() => {
    if (!authed || !pw) return;
    let stopped = false;
    const seen = JSON.parse(localStorage.getItem(LAST_SEEN_KEY) || "{}");
    const since = seen.timestamp || new Date(Date.now() - 7 * 86400 * 1000).toISOString();
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/checkout/admin/unread?since=${encodeURIComponent(since)}`, {
          headers: { "X-Admin-Password": pw },
        });
        if (!stopped) setUnread({ orders: data.orders || 0, quotes: data.quotes || 0 });
      } catch { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 45000);
    return () => { stopped = true; clearInterval(t); };
  }, [authed, pw]);

  const bumpSeenFor = (tabName) => {
    if (tabName !== "orders" && tabName !== "quotes") return;
    const now = new Date().toISOString();
    localStorage.setItem(LAST_SEEN_KEY, JSON.stringify({ timestamp: now }));
    setUnread((u) => ({ ...u, [tabName]: 0 }));
  };

  const verify = useCallback(async (password) => {
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": password } });
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (pw) {
      verify(pw).then((ok) => {
        if (ok) setAuthed(true);
        else { sessionStorage.removeItem(STORAGE_KEY); setPw(""); }
      });
    }
  }, [pw, verify]);

  const login = async (e) => {
    e.preventDefault();
    const ok = await verify(input);
    if (ok) {
      sessionStorage.setItem(STORAGE_KEY, input);
      setPw(input);
      setAuthed(true);
      toast.success("Welcome back.");
    } else {
      toast.error("Incorrect password");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPw(""); setAuthed(false); setInput("");
  };

  if (!authed) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-ink-950 px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-md card-dark p-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-10 bg-gold text-black flex items-center justify-center font-display font-bold">T</span>
            <div>
              <div className="font-display text-lg">Admin</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Toronto Custom Signs</div>
            </div>
          </div>
          <h1 className="font-display text-3xl tracking-tight">Sign in</h1>
          <p className="text-white/55 text-sm mt-3">Enter the admin password to view the dashboard.</p>
          <label className="block mt-7">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">Password</span>
            <div className="relative">
              <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="admin-password-input"
                type="password"
                autoFocus
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="w-full bg-ink-800 border border-white/10 text-white pl-11 pr-4 py-3.5 text-sm rounded-sm outline-none focus:border-gold transition-colors"
                placeholder="••••••••"
              />
            </div>
          </label>
          <button type="submit" data-testid="admin-login-btn" className="btn-gold w-full justify-center mt-6">Sign in</button>
        </form>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-ink-950 pt-10 pb-24" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2 border border-white/10 bg-ink-900 rounded-sm p-1">
            <TabBtn active={tab === "quotes"} onClick={() => { setTab("quotes"); bumpSeenFor("quotes"); }} icon={<Inbox size={14} />} label="Quotes" testId="tab-quotes" badge={unread.quotes} />
            <TabBtn active={tab === "orders"} onClick={() => { setTab("orders"); bumpSeenFor("orders"); }} icon={<Package size={14} />} label="Orders" testId="tab-orders" badge={unread.orders} />
            <TabBtn active={tab === "catalog"} onClick={() => setTab("catalog")} icon={<ShoppingBag size={14} />} label="Shop Catalog" testId="tab-catalog" />
            <TabBtn active={tab === "coupons"} onClick={() => setTab("coupons")} icon={<Ticket size={14} />} label="Coupons" testId="tab-coupons" />
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/leads" data-testid="link-local-leads" className="btn-ghost !py-3 !px-4 border-cyan-400/40 text-cyan-300 hover:bg-cyan-300 hover:text-black transition-colors">
              <MapPinned size={14} /> Local Leads
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="btn-ghost !py-3 !px-4"><LogOut size={14} /> Logout</button>
          </div>
        </div>

        {tab === "quotes" && <QuotesPanel password={pw} onSessionExpired={logout} />}
        {tab === "orders" && <AdminOrders password={pw} />}
        {tab === "catalog" && <AdminCatalog password={pw} />}
        {tab === "coupons" && <AdminCoupons password={pw} />}
      </div>
    </section>
  );
}

function TabBtn({ active, onClick, icon, label, testId, badge }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative flex items-center gap-2 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-colors ${active ? "bg-gold text-black" : "text-white/60 hover:text-gold"}`}
    >
      {icon} {label}
      {badge > 0 && (
        <span
          data-testid={`${testId}-badge`}
          className={`ml-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-semibold rounded-full ${active ? "bg-black text-gold" : "bg-gold text-black"}`}
          title={`${badge} new since your last visit`}
        >{badge > 99 ? "99+" : badge}</span>
      )}
    </button>
  );
}

function QuotesPanel({ password, onSessionExpired }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const headers = useMemo(() => ({ "X-Admin-Password": password }), [password]);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, { headers });
      setQuotes(data);
    } catch (err) {
      if (err.response?.status === 401) {
        onSessionExpired();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Failed to load quotes");
      }
    } finally {
      setLoading(false);
    }
  }, [headers, onSessionExpired]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/quotes/${id}`, { status }, { headers });
      setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, { headers });
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filtered = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: quotes.filter((q) => q.status === s).length }), {});

  const searchQuotes = useCallback(async (query) => {
    const q = query.toLowerCase();
    return quotes
      .filter((qt) =>
        (qt.name || "").toLowerCase().includes(q) ||
        (qt.email || "").toLowerCase().includes(q) ||
        (qt.phone || "").toLowerCase().includes(q) ||
        (qt.business || "").toLowerCase().includes(q) ||
        (qt.message || "").toLowerCase().includes(q),
      )
      .slice(0, 8)
      .map((qt) => ({
        type: "quote",
        id: qt.id,
        title: qt.name || "(no name)",
        subtitle: `${qt.email || ""}${qt.business ? " · " + qt.business : ""} · ${qt.status || "new"}`,
      }));
  }, [quotes]);

  const jumpToQuote = (item) => {
    const el = document.querySelector(`[data-testid='quote-card-${item.id}']`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-gold");
      setTimeout(() => el.classList.remove("ring-2", "ring-gold"), 1600);
    }
  };

  return (
    <div data-testid="admin-quotes-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow gold-bar">Admin</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2 tracking-tight">Quote requests</h1>
        </div>
        <button onClick={fetchQuotes} data-testid="admin-refresh" className="btn-ghost !py-3 !px-4"><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="mb-4" data-testid="quotes-search-wrap">
        <Autocomplete
          variant="admin"
          testId="quotes-search"
          placeholder="Search quotes — name, email, business, message…"
          onQuery={searchQuotes}
          onSelect={jumpToQuote}
          noResultsHint="No quotes match this search."
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-filters">
        <FilterPill label={`All · ${quotes.length}`} active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all" />
        {STATUSES.map((s) => (
          <FilterPill key={s} label={`${s} · ${counts[s] || 0}`} active={filter === s} onClick={() => setFilter(s)} testId={`filter-${s}`} />
        ))}
      </div>

      {loading ? (
        <div className="text-white/50 py-20 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card-dark p-16 text-center text-white/50" data-testid="admin-empty">
          No {filter !== "all" ? filter : ""} quotes yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {filtered.map((q) => (
            <article key={q.id} className="card-dark p-7" data-testid={`quote-card-${q.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-display text-xl">{q.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1 flex items-center gap-2">
                    <Calendar size={11} /> {new Date(q.created_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1 border ${STATUS_STYLES[q.status] || ""}`}>{q.status}</span>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-white/75">
                <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-gold break-all"><Mail size={13} className="text-gold shrink-0" /> {q.email}</a>
                {q.phone && <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-gold"><Phone size={13} className="text-gold" /> {q.phone}</a>}
                {q.company && <span className="flex items-center gap-2"><Building2 size={13} className="text-gold" /> {q.company}</span>}
                {q.location && <span className="flex items-center gap-2"><MapPin size={13} className="text-gold" /> {q.location}</span>}
              </div>

              <dl className="mt-5 grid grid-cols-3 gap-3 text-[11px]">
                <Meta label="Service" value={q.service} />
                <Meta label="Size" value={q.project_size || "—"} />
                <Meta label="Timeline" value={q.timeline || "—"} />
              </dl>

              <p className="mt-5 text-white/70 text-sm leading-relaxed bg-ink-800 p-4 border-l-2 border-gold whitespace-pre-wrap">{q.message}</p>

              <div className="mt-5 flex flex-wrap items-center gap-2 pt-5 border-t border-white/5">
                <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 mr-2">Status:</span>
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(q.id, s)}
                    disabled={q.status === s}
                    data-testid={`set-${s}-${q.id}`}
                    className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${q.status === s ? "bg-gold text-black border-gold cursor-default" : "border-white/15 text-white/60 hover:border-gold hover:text-gold"}`}
                  >{s}</button>
                ))}
                <button
                  onClick={() => remove(q.id)}
                  data-testid={`delete-${q.id}`}
                  className="ml-auto text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1.5"
                ><Trash2 size={12} /> Delete</button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

const FilterPill = ({ label, active, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`px-5 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${active ? "bg-gold text-black border-gold" : "border-white/15 text-white/70 hover:border-gold hover:text-gold"}`}>{label}</button>
);

const Meta = ({ label, value }) => (
  <div>
    <dt className="text-[9px] uppercase tracking-[0.25em] text-white/40">{label}</dt>
    <dd className="text-white/85 mt-1 truncate" title={value}>{value}</dd>
  </div>
);
